/**
 * Sales Agent Nudge Hooks
 * Jotai atoms and hooks for the nudges list, the field catalog and the nudge writes.
 */

import { useAtom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { salesNudgesService } from '@/services/sales-nudges.service';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { invalidateQueries } from '@/lib/query-client';
import type { CreateNudgeDto, NudgeStatus, UpdateNudgeDto } from '@/modules/sales-agent/types';

export const SALES_NUDGES_QUERY_KEY = 'sales-nudges';

const nudgesQueryAtom = atomWithQuery((get) => ({
	// The brand is in the key as well as the request: a brand switch does not clear queries, so a
	// key without it would show the previous brand's nudges under the next one's name.
	queryKey: [SALES_NUDGES_QUERY_KEY, 'list', buScopeKey(get)],
	queryFn: () => salesNudgesService.listNudges(buScopeParams(get)),
	staleTime: 30 * 1000
}));

const nudgeFieldsQueryAtom = atomWithQuery(() => ({
	// The catalog changes by a seed script, not by anything on this page.
	queryKey: [SALES_NUDGES_QUERY_KEY, 'fields'],
	queryFn: () => salesNudgesService.listFields(),
	staleTime: 10 * 60 * 1000
}));

const invalidateNudges = (): Promise<void> => invalidateQueries([SALES_NUDGES_QUERY_KEY, 'list']);

/** Refetches in the background: the caller moves on with the saved nudge the response returned. */
const refreshNudgesInBackground = (): void => {
	void invalidateNudges();
};

const createNudgeMutationAtom = atomWithMutation((get) => ({
	mutationKey: [SALES_NUDGES_QUERY_KEY, 'create'],
	mutationFn: (payload: CreateNudgeDto) => salesNudgesService.createNudge(payload, buScopeParams(get)),
	// Not idempotent: the app-wide retry would POST a second copy when the first landed but its
	// response was lost.
	retry: false,
	onSuccess: refreshNudgesInBackground
}));

const updateNudgeMutationAtom = atomWithMutation((get) => ({
	mutationKey: [SALES_NUDGES_QUERY_KEY, 'update'],
	mutationFn: ({ nudgeId, payload }: { nudgeId: string; payload: UpdateNudgeDto }) =>
		salesNudgesService.updateNudge(nudgeId, payload, buScopeParams(get)),
	onSuccess: refreshNudgesInBackground
}));

const setNudgeStatusMutationAtom = atomWithMutation((get) => ({
	mutationKey: [SALES_NUDGES_QUERY_KEY, 'status'],
	mutationFn: ({ nudgeId, status }: { nudgeId: string; status: NudgeStatus }) =>
		salesNudgesService.setNudgeStatus(nudgeId, status, buScopeParams(get)),
	// Returned, so the mutation stays pending until the list has refetched: the switch reads the
	// list, and settling first would show the old status for a moment before the new one.
	onSuccess: invalidateNudges
}));

const deleteNudgeMutationAtom = atomWithMutation((get) => ({
	mutationKey: [SALES_NUDGES_QUERY_KEY, 'delete'],
	mutationFn: (nudgeId: string) => salesNudgesService.deleteNudge(nudgeId, buScopeParams(get)),
	onSuccess: refreshNudgesInBackground
}));

export function useSalesNudges() {
	const [query] = useAtom(nudgesQueryAtom);
	return query;
}

export function useNudgeFields() {
	const [query] = useAtom(nudgeFieldsQueryAtom);
	return query;
}

export function useCreateNudge() {
	const [mutation] = useAtom(createNudgeMutationAtom);
	return mutation;
}

export function useUpdateNudge() {
	const [mutation] = useAtom(updateNudgeMutationAtom);
	return mutation;
}

export function useSetNudgeStatus() {
	const [mutation] = useAtom(setNudgeStatusMutationAtom);
	return mutation;
}

export function useDeleteNudge() {
	const [mutation] = useAtom(deleteNudgeMutationAtom);
	return mutation;
}
